import api from './ApiInstance';
import ApiRoutes from './ApiRoutes';

export interface ProcessedResult {
  searchQuery: string;
  response: string;
  threadId?: string; // Thread ID returned by backend
}

export interface ConversationMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface QueryHistory {
  id: string;
  query: string;
  result: ProcessedResult;
  threadId?: string; // Thread ID for grouping conversation messages
  filters: {
    facilityNames?: string[];
    classificationNames?: string[];
    classificationTypes?: string[];
    dateRange?: {
      start: string;
      end: string;
    };
  };
  createdDate: string;
  createdBy: string;
}

// Use the API routes from ApiRoutes.js
const API_ENDPOINT = ApiRoutes.azureOpenAI;
const SAVE_QUERY_HISTORY_ENDPOINT = ApiRoutes.saveQueryHistory;
const GET_QUERY_HISTORY_ENDPOINT = ApiRoutes.getQueryHistory;
const GET_QUERY_HISTORY_ITEM_ENDPOINT = ApiRoutes.getQueryHistoryItem;
const GET_QUERY_HISTORY_BY_THREAD_ENDPOINT = ApiRoutes.getQueryHistoryByThread;
const DELETE_QUERY_HISTORY_ENDPOINT = ApiRoutes.deleteQueryHistory;

/**
 * Process a question with the Azure OpenAI service
 *
 * Backend Response Formats Supported:
 * 1. Simple string: "AI response text"
 * 2. Structured object: { response: "AI response text", threadId: "new_thread_id" }
 *
 * If backend returns a threadId, it will be used for subsequent requests
 */
export async function processQuestion(
  query: string,
  threadId?: string,
  facilityNames?: string[],
  classificationNames?: string[],
  classificationTypes?: string[],
  startDate?: string,
  endDate?: string
): Promise<ProcessedResult> {
  try {
    console.log('Processing question:', query);

    const payload = {
      query,
      threadId: threadId || '', // Thread ID at top level to match backend model
      filters: {
        facilityNames: facilityNames || [],
        classificationNames: classificationNames || [],
        classificationTypes: classificationTypes || [],
        startDate: startDate || '',
        endDate: endDate || ''
      }
    };
    
    // Make the API request
    const response = await api.post<typeof payload, any>(API_ENDPOINT, payload);
    console.log('Response received successfully');
    console.log('Response', response);

    // Handle different response formats
    let responseText: string;
    let returnedThreadId: string | undefined;

    if (typeof response === 'string') {
      // Simple string response - but it might be a JSON string, so try to parse it
      try {
        const parsed = JSON.parse(response);
        if (parsed && typeof parsed === 'object' && parsed.response) {
          // The string was a JSON object with a response field
          responseText = parsed.response;
          returnedThreadId = parsed.threadId || parsed.thread_id;
        } else {
          // The string was just plain text
          responseText = response;
        }
      } catch (e) {
        // Not a JSON string, just use it as is
        responseText = response;
      }
    } else if (response && typeof response === 'object') {
      // Structured response with potential thread ID and metadata
      // Extract only the response field, ignore metadata like session_id, client_id, etc.

      if (response.response) {
        // Check if response.response is an object (nested structure)
        if (typeof response.response === 'object' && response.response.response) {
          // Backend returns: { response: { response: "text", session_id: "...", thread_id: "...", ... } }
          // Extract only the inner response text
          responseText = response.response.response;
          returnedThreadId = response.response.threadId || response.response.thread_id;
        } else if (typeof response.response === 'string') {
          // Check if the response field itself is a JSON string
          try {
            const parsed = JSON.parse(response.response);
            if (parsed && typeof parsed === 'object' && parsed.response) {
              // The response field was a JSON string with nested response
              responseText = parsed.response;
              // Also check for thread_id in the nested object
              if (!returnedThreadId) {
                returnedThreadId = parsed.threadId || parsed.thread_id;
              }
            } else {
              // The response field is just a plain string
              responseText = response.response;
            }
          } catch (e) {
            // Not a JSON string, just use it as is
            responseText = response.response;
          }
        } else {
          // response.response is neither object nor string, stringify it
          responseText = JSON.stringify(response.response);
        }
      } else if (response.text && typeof response.text === 'string') {
        responseText = response.text;
      } else {
        // Fallback: if no response/text field, stringify the whole object
        // This should rarely happen with proper backend implementation
        console.warn('Response object missing "response" or "text" field, falling back to JSON.stringify');
        responseText = JSON.stringify(response);
      }

      // Extract thread_id (handle both camelCase and snake_case)
      // Check both top-level and nested response object
      if (!returnedThreadId) {
        returnedThreadId = response.threadId || response.thread_id;
        if (!returnedThreadId && response.response && typeof response.response === 'object') {
          returnedThreadId = response.response.threadId || response.response.thread_id;
        }
      }
    } else {
      responseText = 'No response received from the server.';
    }

    // Create the processed result
    const result: ProcessedResult = {
      searchQuery: query,
      response: responseText,
      ...(returnedThreadId && { threadId: returnedThreadId })
    };
    
    // Only save to database if there is a valid response
    if (response && response !== 'No relevant incidents found for your query') {
      // Save to server
      try {
        await saveQueryToHistory(
          query,
          result,
          returnedThreadId, // Pass the thread ID
          facilityNames,
          classificationNames,
          classificationTypes,
          startDate,
          endDate
        );
      } catch (saveError) {
        console.error('Failed to save query to server:', saveError);
        // Continue execution even if server save fails
      }

      // Save to localStorage as a fallback
      saveQueryToLocalStorage(
        query,
        result,
        returnedThreadId, // Pass the thread ID
        facilityNames,
        classificationNames,
        classificationTypes,
        startDate,
        endDate
      );
    } else {
      console.log('No response received, skipping query history save');
    }
    
    // Return the processed result
    return result;
  } catch (error) {
    console.error('Error processing question:', error);
    
    const errorResult = {
      searchQuery: query,
      response: `Error processing question: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
    
    // Try to save errors to server too for a complete history
    // try {
    //   await saveQueryToHistory(
    //     query,
    //     errorResult
    //   );
    // } catch (saveError) {
    //   console.error('Failed to save error query to server:', saveError);
    //   // Continue execution even if server save fails
    // }

    // Always save errors to localStorage too for a complete history
    // saveQueryToLocalStorage(
    //   query,
    //   errorResult
    // );
    
    return errorResult;
  }
}

/**
 * Saves a query and its result to the history
 */
export async function saveQueryToHistory(
  query: string,
  result: ProcessedResult,
  threadId?: string,
  facilityNames?: string[],
  classificationNames?: string[],
  classificationTypes?: string[],
  startDate?: string,
  endDate?: string
): Promise<void> {
  try {
    const payload = {
      query,
      result,
      threadId: threadId || '', // Include thread ID in payload
      filters: {
        facilityNames,
        classificationNames,
        classificationTypes,
        dateRange: {
          start: startDate,
          end: endDate
        }
      }
    };

    await api.post<typeof payload, void>(SAVE_QUERY_HISTORY_ENDPOINT, payload);
    console.log('Query saved to history successfully with threadId:', threadId);
  } catch (error) {
    console.error('Failed to save query history:', error);
    throw error;
  }
}

/**
 * Gets the query history for the current user
 */
export async function getQueryHistory(): Promise<QueryHistory[]> {
  try {
    return await api.get<null, QueryHistory[]>(GET_QUERY_HISTORY_ENDPOINT);
  } catch (error) {
    console.error('Failed to fetch query history:', error);
    throw error;
  }
}

/**
 * Gets a specific query history item by ID
 */
export async function getQueryHistoryItem(id: string): Promise<QueryHistory> {
  try {
    return await api.get<null, QueryHistory>(`${GET_QUERY_HISTORY_ITEM_ENDPOINT}/${id}`);
  } catch (error) {
    console.error(`Failed to fetch query history item ${id}:`, error);
    throw error;
  }
}

/**
 * Gets all query history items for a specific thread
 */
export async function getQueryHistoryByThread(threadId: string): Promise<QueryHistory[]> {
  try {
    return await api.get<null, QueryHistory[]>(`${GET_QUERY_HISTORY_BY_THREAD_ENDPOINT}/${threadId}`);
  } catch (error) {
    console.error(`Failed to fetch query history for thread ${threadId}:`, error);
    throw error;
  }
}

/**
 * Save query to localStorage as a fallback
 */
export function saveQueryToLocalStorage(
  query: string,
  result: ProcessedResult,
  threadId?: string,
  facilityNames?: string[],
  classificationNames?: string[],
  classificationTypes?: string[],
  startDate?: string,
  endDate?: string
): void {
  try {
    const history = JSON.parse(localStorage.getItem('queryHistory') || '[]');

    history.push({
      id: Date.now().toString(),
      query,
      result,
      threadId: threadId || '', // Include thread ID
      filters: {
        facilityNames,
        classificationNames,
        classificationTypes,
        dateRange: {
          start: startDate,
          end: endDate
        }
      },
      createdDate: new Date().toISOString(),
      createdBy: localStorage.getItem('currentUserEmail') || ''
    });

    // Keep only the most recent 50 items
    if (history.length > 50) {
      history.splice(0, history.length - 50);
    }

    localStorage.setItem('queryHistory', JSON.stringify(history));
  } catch (error) {
    console.error('Failed to save to localStorage:', error);
  }
}

/**
 * Get query history from localStorage
 */
export function getLocalQueryHistory(): QueryHistory[] {
  try {
    return JSON.parse(localStorage.getItem('queryHistory') || '[]');
  } catch (error) {
    console.error('Failed to get from localStorage:', error);
    return [];
  }
}

/**
 * Get a specific query history item from localStorage by ID
 */
export function getLocalQueryHistoryItem(id: string): QueryHistory | null {
  try {
    const history = JSON.parse(localStorage.getItem('queryHistory') || '[]');
    return history.find((item: QueryHistory) => item.id === id) || null;
  } catch (error) {
    console.error(`Failed to get query history item ${id} from localStorage:`, error);
    return null;
  }
}

/**
 * Deletes a query history item by ID from the server
 */
export async function deleteQueryHistory(id: string): Promise<void> {
  try {
    await api.del<null, void>(`${DELETE_QUERY_HISTORY_ENDPOINT}/${id}`);
    console.log(`Query history item ${id} deleted successfully from server`);
    
    // Also delete from localStorage to keep both in sync
    deleteLocalQueryHistoryItem(id);
  } catch (error) {
    console.error(`Failed to delete query history item ${id}:`, error);
    throw error;
  }
}

/**
 * Deletes a query history item from localStorage by ID
 */
export function deleteLocalQueryHistoryItem(id: string): void {
  try {
    const history = JSON.parse(localStorage.getItem('queryHistory') || '[]');
    const filteredHistory = history.filter((item: QueryHistory) => item.id !== id);
    localStorage.setItem('queryHistory', JSON.stringify(filteredHistory));
    console.log(`Query history item ${id} deleted from localStorage`);
  } catch (error) {
    console.error(`Failed to delete query history item ${id} from localStorage:`, error);
  }
}
